import { Box, InputBase, Typography, Tabs, Tab, IconButton, alpha } from "@mui/material";
import { Search, Close } from "@mui/icons-material";
import { useState, useEffect, useMemo } from "react";
import { useFetchCategorias } from "../../../../../produtos/hooks/useFetchCategorias";
import { useChatPdVContext } from "../../../../contexts/atendimento/ChatPdVContext";
import { TipoMarketplace } from "../../../../../marketplace/enum/TipoMarketplace";
import { useLetras } from "../../../../../core/hooks/useLetras";
import { IProduto } from "../../../../../produtos/interfaces/IProduto";
import { ICategoria } from "../../../../../produtos/interfaces/ICategoria";
import moment from "moment";
import { DiaSemanaOrdem } from "../../../../../core/enum/DiaSemana";
import { ChatPdVProdutoLinha } from "../../ChatPdVProdutoLinha";
import { contorno, cores, limitarLinhas, raio, superficie, texto } from "../../identidade";

interface ChatPdVCatalogoProps {
    onFecharDrawer?: () => void;
}

export const ChatPdVCatalogo = ({ onFecharDrawer }: ChatPdVCatalogoProps) => {
    const { categorias } = useFetchCategorias();
    const {
        adicionarProduto,
        adicionarProdutosCombo,
        tipo,
        pedidosEmAndamento,
        exibirNovoPedido,
        voltarTela,
        navegarParaFormPizza,
        navegarParaFormPreparado,
        navegarParaFormPeso,
    } = useChatPdVContext();

    const [categoria, setCategoria] = useState<ICategoria | undefined>(undefined);
    const [pesquisa, setPesquisa] = useState<string>("");
    const [pesquisaDebounced, setPesquisaDebounced] = useState<string>("");
    const { maiusculas, removerAcentos, removerCedilha } = useLetras();

    // Debounce para o filtro (igual ao OperacaoCategoriasProdutosContainer)
    useEffect(() => {
        const timer = setTimeout(() => {
            setPesquisaDebounced(pesquisa);
        }, 300);
        
        return () => clearTimeout(timer);
    }, [pesquisa]);

    const diaAtual = moment().day();
    const horaAtual = moment();

    // Filtrar categorias por período de exibição (igual ao OperacaoCategoriasProdutosContainer)
    const categoriasFiltradas = useMemo(() => {
        const filtradas = categorias?.filter(cat => {
            if (!cat.periodosExibicao?.length) {
                return true;
            }

            return cat.periodosExibicao.some(periodo => {
                if (DiaSemanaOrdem[periodo.diaSemana] !== diaAtual) {
                    return false;
                }

                return periodo.horarios.some(horario => {
                    const horaInicio = moment(horario.inicio, 'HH:mm');
                    const horaFim = moment(horario.fim, 'HH:mm');

                    return horaAtual.isBetween(horaInicio, horaFim);
                });
            });
        });

        return filtradas || [];
    }, [categorias]);

    // Produtos ativos da categoria selecionada (igual ao OperacaoCategoriasProdutosContainer)
    const produtosAtivos = categoria?.produtos?.filter(produto => !produto.inativadoAs && !produto.apenasCombo);

    // Selecionar primeira categoria válida (PDV + contexto) ao carregar
    useEffect(() => {
        if (categoriasFiltradas && categoriasFiltradas.length) {
            const indexCategoria = categoriasFiltradas.findIndex(cat =>
                cat?.contextos?.includes(tipo) &&
                cat?.marketplaces &&
                (cat?.marketplaces?.filter(marketplace => marketplace.tipo === TipoMarketplace.PDV).length) > 0
            );
            if (indexCategoria !== -1) {
                setCategoria(categoriasFiltradas[indexCategoria]);
            }
        }
    }, [categoriasFiltradas]);

    // Resetar categoria quando limpa pesquisa (igual ao OperacaoCategoriasProdutosContainer)
    useEffect(() => {
        if (pesquisa?.length) {
            setCategoria(undefined);
            return;
        } else {
            setTimeout(() => {
                const indexCategoria = categoriasFiltradas.findIndex(cat =>
                    cat?.contextos?.includes(tipo) &&
                    cat?.marketplaces &&
                    (cat?.marketplaces?.filter(marketplace => marketplace.tipo === TipoMarketplace.PDV).length) > 0
                );
                if (indexCategoria !== -1 && !categoria) {
                    setCategoria(categoriasFiltradas[indexCategoria]);
                }
            }, 500);
        }
    }, [categoria, pesquisa]);

    // Filtrar produtos por pesquisa (igual ao OperacaoCategoriasProdutosContainer)
    const produtosFiltrados = useMemo(() => {
        const produtosList: IProduto[] = [];

        if (!pesquisaDebounced?.length) {
            return produtosList;
        }

        categoriasFiltradas.forEach(cat => {
            const categoriaPDV = cat.marketplaces?.filter(marketplace => marketplace.tipo === TipoMarketplace.PDV);
            if (categoriaPDV?.length && cat.contextos?.includes(tipo)) {
                (cat.produtos?.filter(produto => {
                    const strDescricao = removerAcentos(removerCedilha(maiusculas(produto.descricao)));
                    const strPesquisa = removerAcentos(removerCedilha(maiusculas(pesquisaDebounced)));

                    return strDescricao.includes(strPesquisa) && !produto.inativadoAs && !produto.apenasCombo;
                }) ?? [])
                .forEach(produto => produtosList.push(produto));
            }
        });

        return produtosList;
    }, [categoriasFiltradas, pesquisaDebounced, tipo]);

    const handleChangeCategoria = (_event: React.SyntheticEvent, novaTab: string) => {
        setPesquisa('');
        setCategoria(categorias?.find(cat => cat.uuid === novaTab));
    };

    // Se há pedidos em andamento e não foi escolhido exibir novo pedido, não mostrar catálogo
    const mostrarPedidosEmAndamento = pedidosEmAndamento && pedidosEmAndamento.length > 0 && !exibirNovoPedido;

    if (mostrarPedidosEmAndamento) {
        return null;
    }

    // Produtos a exibir: filtrados (se pesquisa) ou ativos da categoria
    const produtosParaExibir = produtosFiltrados.length ? produtosFiltrados : produtosAtivos;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                backgroundColor: 'background.paper',
            }}
        >
            {/* Header com botão de fechar */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: theme => contorno(theme, 0.09),
                    flexShrink: 0,
                }}
            >
                <Typography sx={{ ...texto.secao, color: 'text.secondary' }}>
                    Adicionar itens
                </Typography>
                <IconButton onClick={onFecharDrawer ?? voltarTela} size="small">
                    <Close />
                </IconButton>
            </Box>

            {/* Área de Produtos */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 1.5,
                    pb: 0.5,
                    minHeight: 0,
                }}
            >
                {/* Header: Tabs */}
                <Tabs
                    value={categoria?.uuid || false}
                    onChange={handleChangeCategoria}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 36,
                        mb: 1.25,
                        flexShrink: 0,
                        '& .MuiTabs-indicator': { backgroundColor: cores.marca[500], height: 2 },
                        '& .MuiTab-root': {
                            minHeight: 36,
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'none',
                            padding: '0.4rem 0.85rem',
                            minWidth: 'auto',
                            color: 'text.secondary',
                            '&.Mui-selected': { color: cores.marca[500] },
                        },
                    }}
                >
                    {categoriasFiltradas?.map(cat => {
                        const categoriaPDV = cat.marketplaces?.filter(marketplace => marketplace.tipo === TipoMarketplace.PDV);
                        if (cat?.contextos?.includes(tipo) === true && categoriaPDV?.length && cat.uuid) {
                            return (
                                <Tab
                                    key={cat.uuid}
                                    value={cat.uuid}
                                    label={cat.descricao}
                                />
                            );
                        }
                        return null;
                    })}
                </Tabs>

                {/* Campo de Pesquisa */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        height: 38,
                        px: 1.25,
                        mb: 1.25,
                        flexShrink: 0,
                        borderRadius: raio.sm,
                        backgroundColor: theme => superficie(theme, 0.05),
                        border: theme => contorno(theme, 0.08),
                        transition: theme => theme.transitions.create(['border-color', 'background-color'], { duration: 160 }),
                        '&:focus-within': {
                            borderColor: alpha(cores.marca[500], 0.45),
                            backgroundColor: alpha(cores.marca[500], 0.05),
                        },
                    }}
                >
                    <Search sx={{ fontSize: 17, color: 'text.secondary' }} />
                    <InputBase
                        // O catálogo abre com o cursor aqui: o operador digita, dá
                        // TAB e já está no primeiro produto.
                        autoFocus
                        placeholder="Buscar produto"
                        value={pesquisa}
                        onChange={(e) => setPesquisa(e.target.value)}
                        sx={{ flex: 1, ...texto.corpo }}
                    />
                    {!!pesquisa && (
                        // Fora da ordem de TAB de propósito: senão ele se enfia
                        // entre a busca e o primeiro produto e come um TAB do
                        // operador. Continua clicável.
                        <IconButton size="small" tabIndex={-1} onClick={() => setPesquisa('')} aria-label="limpar busca">
                            <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                    )}
                </Box>

                {/* Grid de Produtos */}
                <Box 
                    sx={{ 
                        flex: 1, 
                        overflow: 'auto',
                        alignContent: 'flex-start',
                    }}
                >
                    {!produtosParaExibir || produtosParaExibir.length === 0 ? (
                        <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                            <Typography sx={{ ...texto.corpo, color: 'text.secondary', ...limitarLinhas(2) }}>
                                {pesquisaDebounced ? 'Nenhum produto encontrado' : 'Selecione uma categoria'}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 1 }}>
                            {produtosParaExibir.map((produto) => (
                                <ChatPdVProdutoLinha
                                    key={produto.uuid}
                                    produto={produto}
                                    adicionarProduto={adicionarProduto}
                                    adicionarProdutosCombo={adicionarProdutosCombo}
                                    onPersonalizarPizza={navegarParaFormPizza}
                                    onPersonalizarPreparado={navegarParaFormPreparado}
                                    onPersonalizarPeso={navegarParaFormPeso}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
